import { resolveDiscoveryIconsBucketKey } from '../../services/admin/discoveryIconsApi';
import { loadLocalBotIcon } from './botIconLocalStorage';
import { loadLocalDiscoveryIcons } from './discoveryIconsLocalStorage';

export const BUCKET_BOTS_BASE = 'https://my.edu.gov.il/bucketfiles/bots';

export const EXTRA_EXPORT_IMAGE_KEYS = [
  'regulatoryBotIcon',
  'pedagogyBotIcon',
  'icon',
];

/** Image fields rewritten + packed into the domain ZIP. */
export const EXPORT_DOMAIN_IMAGE_KEYS = [
  'botIconUrl',
  'subjectIconUrl',
  ...EXTRA_EXPORT_IMAGE_KEYS,
];

export function getBucketBotsBase() {
  const base = window.config?.baseApiUrl;
  return `${String(base).replace(/\/$/, '')}/bucketfiles/bots`;
}

/** Append/replace ?ver= so browsers refetch overwritten bucket assets. Not stored as a new file. */
export function withImageCacheBust(url) {
  if (typeof url !== 'string' || !url.trim()) return url;
  const trimmed = url.trim();
  if (trimmed.startsWith('data:')) return trimmed;
  const withoutVer = trimmed
    .replace(/([?&])ver=\d+/g, '$1')
    .replace(/[?&]$/, '')
    .replace(/\?&/, '?')
    .replace(/&&+/g, '&');
  const separator = withoutVer.includes('?') ? '&' : '?';
  return `${withoutVer}${separator}ver=${Date.now()}`;
}

function isDataUrl(url) {
  return typeof url === 'string' && url.startsWith('data:');
}

function normalizeFileName(name) {
  if (typeof name !== 'string') return '';
  const trimmed = name.trim();
  if (!trimmed) return '';
  return trimmed.replace(/\.(svg|png|jpe?g|webp|gif)$/i, '');
}

function extensionFromMimeType(mimeType) {
  if (mimeType === 'image/png') return 'png';
  if (mimeType === 'image/jpeg' || mimeType === 'image/jpg') return 'jpg';
  if (mimeType === 'image/webp') return 'webp';
  if (mimeType === 'image/gif') return 'gif';
  return 'svg';
}

function exportFolderKey(domainName) {
  if (typeof domainName === 'string' && domainName.trim()) {
    return domainName.trim();
  }
  return 'unknown';
}

const DEFAULT_EXPORT_FILE_NAMES = {
  botIconUrl: 'icon.png',
  subjectImageUrl: 'subject.png',
  subjectIconUrl: 'subjectIcon.png',
  regulatoryBotIcon: 'regulatoryBotIcon.png',
  pedagogyBotIcon: 'pedagogyBotIcon.png',
  icon: 'icon.png',
};

export function fileNameFromUrl(url, fallback = 'icon.png') {
  if (typeof url !== 'string' || !url || isDataUrl(url)) {
    return fallback;
  }
  try {
    let last = decodeURIComponent(
      new URL(url).pathname.split('/').filter(Boolean).pop() || ''
    );
    last = last.replace(/\.bin$/i, '');
    if (last && /\.(svg|png|jpe?g|webp|gif)$/i.test(last)) {
      return last;
    }
  } catch {
    // ignore invalid URLs
  }
  return fallback;
}

export function buildDomainAssetExportUrl(domainName, fileName) {
  const raw = typeof fileName === 'string' ? fileName.trim() : '';
  const baseName = raw.replace(/^.*[\\/]/, '') || 'icon.png';
  return `${getBucketBotsBase()}/${exportFolderKey(domainName)}/assets/${baseName}`;
}

export function buildBotIconExportUrl(domainName, fileName, mimeType) {
  if (typeof fileName === 'string' && /\.(svg|png|jpe?g|webp|gif)$/i.test(fileName.trim())) {
    return buildDomainAssetExportUrl(domainName, fileName);
  }
  const normalized = normalizeFileName(fileName) || 'icon';
  const ext = extensionFromMimeType(mimeType);
  return buildDomainAssetExportUrl(domainName, `${normalized}.${ext}`);
}

export function buildDiscoveryIconExportUrl(domainName, fileName) {
  const raw = typeof fileName === 'string' ? fileName.trim() : '';
  let baseName = raw.replace(/^.*[\\/]/, '').replace(/\.bin$/i, '') || 'icon.svg';
  if (!/\.(svg|png|jpe?g|webp|gif)$/i.test(baseName)) {
    baseName = `${normalizeFileName(baseName) || 'icon'}.svg`;
  }
  return `${getBucketBotsBase()}/${exportFolderKey(domainName)}/assets/${baseName}`;
}

export function rewriteToDomainAssetUrl(url, domainName, fallbackFileName) {
  if (typeof url !== 'string' || !url || isDataUrl(url)) {
    return url;
  }
  const fileName = fileNameFromUrl(url, fallbackFileName || 'icon.png');
  return buildDomainAssetExportUrl(domainName, fileName);
}

function isBotDomainAssetsUrl(url) {
  if (typeof url !== 'string' || !url) return false;
  return /\/bucketfiles\/bots\/[^/]+\/assets\//i.test(url);
}

export function loadDiscoveryIconsForExport(botName, domainName, draft) {
  const folder = exportFolderKey(domainName);
  const legacyKey = resolveDiscoveryIconsBucketKey(botName, draft);
  const byUrl = new Map();
  loadLocalDiscoveryIcons(folder).forEach((icon) => {
    byUrl.set(icon.url, icon);
  });
  if (legacyKey && legacyKey !== folder) {
    loadLocalDiscoveryIcons(legacyKey).forEach((icon) => {
      if (!byUrl.has(icon.url)) {
        byUrl.set(icon.url, icon);
      }
    });
  }
  return Array.from(byUrl.values());
}

function rewriteDiscoveryIcons(list, folder, iconByUrl) {
  return list.map((btn) => {
    if (!btn || typeof btn !== 'object') {
      return btn;
    }

    let nextBtn = btn;
    if (isDataUrl(btn.icon)) {
      const meta = iconByUrl.get(btn.icon);
      const fileName = meta?.fileName || meta?.label || 'icon.svg';
      nextBtn = {
        ...btn,
        icon: buildDiscoveryIconExportUrl(folder, fileName),
      };
    } else if (typeof btn.icon === 'string' && btn.icon) {
      if (/^https?:\/\//i.test(btn.icon) || isBotDomainAssetsUrl(btn.icon)) {
        const fileName = fileNameFromUrl(btn.icon, '');
        if (fileName) {
          nextBtn = {
            ...btn,
            icon: buildDiscoveryIconExportUrl(folder, fileName),
          };
        }
      } else {
        nextBtn = {
          ...btn,
          icon: buildDiscoveryIconExportUrl(
            folder,
            fileNameFromUrl(btn.icon, 'icon.svg')
          ),
        };
      }
    }

    if (Array.isArray(nextBtn.children) && nextBtn.children.length > 0) {
      nextBtn = {
        ...nextBtn,
        children: rewriteDiscoveryIcons(nextBtn.children, folder, iconByUrl),
      };
    }

    return nextBtn;
  });
}

export function resolveExportImageUrls(payload, { botName, domainName, draft }) {
  if (!payload || typeof payload !== 'object') {
    return payload;
  }

  const next = JSON.parse(JSON.stringify(payload));
  const folder = exportFolderKey(domainName);

  EXPORT_DOMAIN_IMAGE_KEYS.forEach((assetKey) => {
    const value = next[assetKey];
    if (typeof value !== 'string' || !value) return;

    if (isDataUrl(value)) {
      const local = loadLocalBotIcon(domainName, botName, assetKey);
      const fileName =
        local?.fileName || DEFAULT_EXPORT_FILE_NAMES[assetKey] || 'icon.png';
      const mimeType = local?.mimeType || 'image/svg+xml';
      next[assetKey] = buildBotIconExportUrl(folder, fileName, mimeType);
      return;
    }

    next[assetKey] = rewriteToDomainAssetUrl(
      value,
      folder,
      DEFAULT_EXPORT_FILE_NAMES[assetKey] || 'icon.png'
    );
  });

  const localIcons = loadDiscoveryIconsForExport(botName, domainName, draft);
  const iconByUrl = new Map(localIcons.map((icon) => [icon.url, icon]));

  if (Array.isArray(next.discoveryButton)) {
    next.discoveryButton = rewriteDiscoveryIcons(
      next.discoveryButton,
      folder,
      iconByUrl
    );
  }

  return next;
}
